import React, { useEffect, useState, useCallback } from 'react';
import {
  Sparkles, RefreshCw, Search, ChevronDown, ChevronUp,
  AlertTriangle, CheckCircle2, TrendingUp, TrendingDown, Loader2, Info,
  Layers, Pickaxe
} from 'lucide-react';
import { useAuth, authFetch } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const LABEL_STYLES = {
  'Top Pick':                 'text-emerald-300 bg-emerald-500/15 border-emerald-500/40',
  'Smart Buy':                'text-green-300 bg-green-500/15 border-green-500/40',
  'Buy / Accumulate':         'text-lime-300 bg-lime-500/15 border-lime-500/40',
  'Watchlist':                'text-yellow-300 bg-yellow-500/15 border-yellow-500/40',
  'Speculative / High Risk':  'text-orange-300 bg-orange-500/15 border-orange-500/40',
  'Avoid':                    'text-red-300 bg-red-500/15 border-red-500/40',
};

const ENTRY_STYLES = {
  'Excellent Entry':              'text-emerald-300 bg-emerald-500/10',
  'Good Entry':                   'text-green-300 bg-green-500/10',
  'Wait for Pullback':            'text-yellow-300 bg-yellow-500/10',
  'Wait for Trend Confirmation':  'text-amber-300 bg-amber-500/10',
  'Avoid Entry':                  'text-red-300 bg-red-500/10',
};

const LABELS = ['All', 'Top Pick', 'Smart Buy', 'Buy / Accumulate', 'Watchlist', 'Speculative / High Risk', 'Avoid'];
const SECTORS = ['All Sectors', 'Technology', 'Healthcare', 'Financials', 'Industrials', 'Consumer', 'Materials', 'Energy', 'Real Estate', 'Utilities', 'Communication Services'];
const ENTRIES = ['All', 'Excellent Entry', 'Good Entry', 'Wait for Pullback', 'Wait for Trend Confirmation', 'Avoid Entry'];
const COMMODITIES = ['All', 'Gold', 'Copper', 'Lithium', 'Uranium', 'Iron Ore', 'Coal', 'Rare Earths', 'Nickel', 'Oil & Gas', 'Silver', 'Diversified', 'Unknown'];
const MINING_STAGES = ['All', 'Major Producer', 'Mid-Tier Producer', 'Small Producer', 'Developer', 'Explorer', 'Unknown'];

function fmtPct(v, digits = 1) {
  if (v === null || v === undefined) return 'N/A';
  return `${v.toFixed(digits)}%`;
}

function fmtPrice(v) {
  if (v === null || v === undefined) return 'N/A';
  return `$${Number(v).toFixed(2)}`;
}

function ScoreBar({ score, label, weight }) {
  const pct = Math.max(0, Math.min(100, score || 0));
  const color = pct >= 70 ? 'bg-emerald-500' : pct >= 50 ? 'bg-yellow-500' : pct >= 30 ? 'bg-orange-500' : 'bg-red-500';
  return (
    <div className="space-y-1" data-testid={`v2-bar-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex justify-between items-baseline text-[11px]">
        <span className="text-muted-foreground">{label} <span className="opacity-60">({Math.round(weight * 100)}%)</span></span>
        <span className="font-mono font-semibold">{pct.toFixed(0)}</span>
      </div>
      <div className="h-1.5 bg-card-hover rounded-full overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/**
 * Derive the specific reasons why Entry Quality = "Avoid Entry".
 * Mirrors backend `compute_entry_quality` triggers + adds contextual signals
 * (severe decline, commodity cap) that help the user understand a
 * Watchlist + Avoid Entry combination.
 */
function getAvoidEntryReasons(stock) {
  const reasons = [];
  const breakdown = stock.score_breakdown || {};
  const trend = breakdown.trend?.detail || {};
  const fin = breakdown.financial_strength?.detail || {};
  const riskDetail = breakdown.risk_quality?.detail || {};
  const deductions = riskDetail.deductions || [];
  const capReasons = stock.cap_reason || [];

  // ---- Direct Avoid-Entry triggers (mirror backend compute_entry_quality)

  // 1. Hard "Avoid" label
  if (stock.final_label === 'Avoid') {
    reasons.push('Stock has an Avoid label — entry blocked regardless of timing.');
  }

  // 2. High debt (D/E > 1.5)
  const hasHighDebtCap = capReasons.some(r => /Debt\/Equity\s*>\s*1\.5/i.test(r));
  if (hasHighDebtCap) {
    reasons.push('Dangerous debt level (Debt/Equity > 1.5).');
  }

  // 3. Negative FCF + OCF (both)
  const hasCashCap = capReasons.some(r => /Negative free .* operating cash/i.test(r));
  const hasFcfDeduction = deductions.some(d => Array.isArray(d) && /Negative free cash/i.test(d[0] || ''));
  const hasOcfNegDeduction = deductions.some(d => Array.isArray(d) && /Operating cash flow/i.test(d[0] || ''));
  if (hasCashCap || (hasFcfDeduction && hasOcfNegDeduction)) {
    reasons.push('Both free cash flow and operating cash flow are negative — company is burning cash.');
  } else if (hasFcfDeduction) {
    reasons.push('Negative free cash flow — company is not generating cash after capex.');
  }

  // 4. Weak fundamentals (FS < 40% — direct Avoid Entry trigger)
  const fsPct = fin.score_pct;
  const fsHasData = (fin.max_score || 0) > 0;
  if (fsHasData && fsPct !== undefined && fsPct < 40) {
    reasons.push(`Weak fundamentals — Financial Strength is ${fsPct.toFixed(0)}% (below the 40% safety threshold).`);
  } else if (fsHasData && fsPct !== undefined && fsPct < 50) {
    // Borderline FS — informational only, helps explain Watchlist cap
    reasons.push(`Borderline-weak fundamentals — Financial Strength is ${fsPct.toFixed(0)}% (caps the label at Watchlist).`);
  }

  // ---- Contextual signals (severity / mining-specific)

  // 5a. Severe short-term decline (standalone, no MA gate)
  const r3 = trend.return_3m;
  const r6 = trend.return_6m;
  const r1m = trend.return_1m;
  if (r3 !== null && r3 !== undefined && r3 <= -25) {
    reasons.push(`Severe 3M decline (${r3.toFixed(1)}%) — sharp short-term sell-off, wait for stabilisation.`);
  } else if (r6 !== null && r6 !== undefined && r6 <= -40) {
    reasons.push(`Severe 6M decline (${r6.toFixed(1)}%) — sustained selling pressure.`);
  } else if (r1m !== null && r1m !== undefined && r1m <= -20) {
    reasons.push(`Severe 1M decline (${r1m.toFixed(1)}%) — rapid recent drawdown.`);
  }

  // 5b. Below both MAs (only if not already covered by severe decline)
  const below50 = trend.above_50dma === false;
  const below200 = trend.above_200dma === false;
  const alreadyCoveredByDecline = (r3 !== null && r3 !== undefined && r3 <= -25);
  if (below50 && below200 && !alreadyCoveredByDecline) {
    reasons.push('Price is trading below both 50DMA and 200DMA — no trend support.');
  }

  // 6. Mining-specific: weak commodity cycle
  const cts = stock.commodity_trend_score;
  if (stock.is_resource_sector && cts !== null && cts !== undefined && cts < 35) {
    reasons.push(`Weak commodity cycle — ${stock.primary_commodity || 'commodity'} trend score is ${cts.toFixed(0)} (below 35).`);
  }

  // 7. Mining-specific: dilution risk
  if (stock.is_resource_sector && stock.dilution_risk_flag) {
    const pct = stock.dilution_pct_yoy;
    reasons.push(`High share dilution${pct != null ? ` (${pct.toFixed(0)}% YoY)` : ''} — typical of cash-burning explorers.`);
  }

  // ---- Fallback: if engine says Avoid Entry but none of the above matched,
  // surface the raw cap_reasons so the user always sees *something*.
  if (reasons.length === 0 && capReasons.length > 0) {
    capReasons.forEach(r => reasons.push(r));
  }
  if (reasons.length === 0) {
    reasons.push('The engine flagged this stock for Avoid Entry. See Risk Flags and Score Capped sections below for details.');
  }

  return reasons;
}

function StockCard({ stock, expanded, onToggle, isAdmin = false, onMiningOverride = null }) {
  const labelClass = LABEL_STYLES[stock.final_label] || 'text-gray-300 bg-gray-500/15 border-gray-500/30';
  const entryClass = ENTRY_STYLES[stock.entry_quality] || 'text-gray-300 bg-gray-500/10';
  const change = stock.daily_change_pct;
  const breakdown = stock.score_breakdown || {};
  const trendDetail = breakdown.trend?.detail || {};
  const finDetail = breakdown.financial_strength?.detail || {};
  const analystDetail = breakdown.analyst?.detail || {};
  const valueDetail = breakdown.value_recovery?.detail || {};

  return (
    <div
      className="bg-card border border-border rounded-xl overflow-hidden transition-all hover:border-emerald-500/40"
      data-testid={`v2-card-${stock.symbol}`}
    >
      {/* HEADER ROW */}
      <button
        onClick={onToggle}
        className="w-full p-4 text-left flex items-start gap-4 hover:bg-card-hover transition-colors"
        data-testid={`v2-toggle-${stock.symbol}`}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-lg font-bold tracking-tight">{stock.symbol}</span>
            <span className="text-xs text-muted-foreground truncate">{stock.name}</span>
            {stock.sector && stock.sector !== 'Unknown' && (
              <span className="text-[10px] px-1.5 py-0.5 bg-card-hover rounded text-muted-foreground">{stock.sector}</span>
            )}
          </div>
          <div className="flex items-center gap-3 flex-wrap text-sm">
            <span className="font-mono">{fmtPrice(stock.current_price)}</span>
            {change !== null && change !== undefined && (
              <span className={`flex items-center gap-0.5 text-xs font-mono ${change >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {change >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                {change >= 0 ? '+' : ''}{change.toFixed(2)}%
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-col items-end gap-2 flex-shrink-0">
          <span className={`px-2.5 py-1 rounded-full text-xs font-bold border ${labelClass}`}>
            {stock.final_label}
          </span>
          <div className="text-right">
            <div className="text-2xl font-bold leading-none">{Math.round(stock.final_score)}</div>
            <div className="text-[10px] text-muted-foreground">Smart Buy V2</div>
          </div>
        </div>
        <div className="flex-shrink-0 self-center text-muted-foreground">
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </button>

      {/* QUICK SIGNAL ROW */}
      <div className="px-4 pb-3 flex items-center gap-2 flex-wrap text-[11px]">
        <span className={`px-2 py-0.5 rounded-full font-medium ${entryClass}`}>
          {stock.entry_quality}
        </span>
        {analystDetail.buy_count > 0 && (
          <span className="text-muted-foreground">
            {analystDetail.buy_count} buys · upside {fmtPct(analystDetail.potential_growth_pct)}
          </span>
        )}
        {finDetail.score_pct > 0 && (
          <span className="text-muted-foreground">· FS {finDetail.score_pct.toFixed(0)}%</span>
        )}
        {trendDetail.return_3m !== null && trendDetail.return_3m !== undefined && (
          <span className={`text-muted-foreground ${trendDetail.return_3m >= 0 ? '' : ''}`}>
            · 3M {fmtPct(trendDetail.return_3m)}
          </span>
        )}
      </div>

      {/* EXPANDED DETAIL */}
      {expanded && (
        <div className="border-t border-border p-4 space-y-4 bg-background/40" data-testid={`v2-detail-${stock.symbol}`}>
          {/* AI Insight */}
          <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <Sparkles className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm leading-relaxed text-emerald-100/90">{stock.ai_insight}</p>
            </div>
          </div>

          {/* Why Avoid Entry? — explainer for Avoid Entry cases (especially Watchlist + Avoid Entry) */}
          {stock.entry_quality === 'Avoid Entry' && (() => {
            const avoidReasons = getAvoidEntryReasons(stock);
            if (avoidReasons.length === 0) return null;
            return (
              <div
                className="bg-red-500/10 border border-red-500/30 rounded-lg p-3"
                data-testid={`v2-avoid-entry-${stock.symbol}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
                  <span className="text-xs font-semibold text-red-300">Why Avoid Entry?</span>
                </div>
                <ul className="space-y-1.5">
                  {avoidReasons.map((r, i) => (
                    <li key={i} className="text-xs text-red-100/90 flex gap-2 leading-relaxed">
                      <span className="text-red-400 flex-shrink-0">•</span>
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
                {stock.final_label === 'Watchlist' && (
                  <p className="text-[10px] text-red-200/70 mt-2 italic">
                    Label is Watchlist (monitor), but at least one entry-blocking condition is active — do not enter until the trigger above is resolved.
                  </p>
                )}
              </div>
            );
          })()}

          {/* Phase 2: Sector adjustment summary */}
          {(stock.base_final_score !== undefined && stock.base_final_score !== stock.final_score) && (
            <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-3" data-testid={`v2-sector-adj-${stock.symbol}`}>
              <div className="flex items-center gap-2 mb-1">
                <Layers className="w-3.5 h-3.5 text-blue-400" />
                <span className="text-xs font-semibold text-blue-400">Sector Adjustment</span>
              </div>
              <div className="text-[11px] text-blue-200/90">
                Base score: <span className="font-mono">{stock.base_final_score?.toFixed(1)}</span>
                {' → '}
                Sector adjusted: <span className="font-mono">{stock.sector_adjusted_score?.toFixed(1)}</span>
                <span className={`ml-2 font-mono ${stock.sector_adjustment > 0 ? 'text-emerald-300' : stock.sector_adjustment < 0 ? 'text-red-300' : ''}`}>
                  ({stock.sector_adjustment > 0 ? '+' : ''}{stock.sector_adjustment})
                </span>
              </div>
              {stock.sector_warning && (
                <p className="text-[11px] text-blue-200/80 mt-1">{stock.sector_warning}</p>
              )}
            </div>
          )}

          {/* Phase 2: Mining-specific block */}
          {stock.is_resource_sector && (
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3" data-testid={`v2-mining-${stock.symbol}`}>
              <div className="flex items-center gap-2 mb-2">
                <Pickaxe className="w-3.5 h-3.5 text-amber-400" />
                <span className="text-xs font-semibold text-amber-400">Mining / Resources</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
                <div>
                  <div className="text-muted-foreground text-[10px]">Stage</div>
                  <div className="font-medium">{stock.mining_stage || 'Unknown'}</div>
                </div>
                <div>
                  <div className="text-muted-foreground text-[10px]">Primary Commodity</div>
                  <div className="font-medium">{stock.primary_commodity || 'Unknown'}</div>
                </div>
                <div>
                  <div className="text-muted-foreground text-[10px]">Commodity Trend</div>
                  <div className="font-mono">{stock.commodity_trend_score != null ? stock.commodity_trend_score.toFixed(0) : 'N/A'}</div>
                </div>
                <div>
                  <div className="text-muted-foreground text-[10px]">Production Quality</div>
                  <div className="font-mono">{stock.production_quality_score != null ? stock.production_quality_score.toFixed(0) : 'N/A'}</div>
                </div>
              </div>
              {stock.dilution_risk_flag && (
                <p className="text-[11px] text-red-300 mt-2">
                  <AlertTriangle className="w-3 h-3 inline mr-1" />
                  Dilution risk flagged ({stock.dilution_pct_yoy != null ? `${stock.dilution_pct_yoy.toFixed(0)}% YoY` : ''})
                </p>
              )}
              {isAdmin && onMiningOverride && (
                <div className="mt-3 pt-3 border-t border-amber-500/15 flex items-center gap-2">
                  <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Admin override</span>
                  <select
                    defaultValue={stock.mining_stage || 'Unknown'}
                    onChange={(e) => onMiningOverride(stock.symbol, e.target.value)}
                    className="px-2 py-1 bg-card-hover border border-border rounded text-[11px] focus:outline-none"
                    data-testid={`v2-mining-override-${stock.symbol}`}
                  >
                    {['Major Producer', 'Mid-Tier Producer', 'Small Producer', 'Developer', 'Explorer', 'Unknown'].map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => onMiningOverride(stock.symbol, null)}
                    className="text-[11px] px-2 py-1 text-muted-foreground hover:text-foreground"
                    title="Clear override and revert to heuristic"
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Score breakdown grid */}
          <div>
            <h4 className="text-xs uppercase tracking-wider text-muted-foreground mb-2 font-semibold">Score Breakdown</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <ScoreBar score={breakdown.analyst?.score} label="Analyst" weight={breakdown.analyst?.weight || 0.3} />
              <ScoreBar score={breakdown.value_recovery?.score} label="Value / Recovery" weight={breakdown.value_recovery?.weight || 0.2} />
              <ScoreBar score={breakdown.financial_strength?.score} label="Financial Strength" weight={breakdown.financial_strength?.weight || 0.2} />
              <ScoreBar score={breakdown.activity?.score} label="Activity" weight={breakdown.activity?.weight || 0.1} />
              <ScoreBar score={breakdown.trend?.score} label="Trend" weight={breakdown.trend?.weight || 0.1} />
              <ScoreBar score={breakdown.risk_quality?.score} label="Risk / Quality" weight={breakdown.risk_quality?.weight || 0.1} />
            </div>
          </div>

          {/* Trend mini stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <div className="bg-card-hover rounded p-2">
              <div className="text-muted-foreground text-[10px]">50DMA</div>
              <div className="font-mono">{trendDetail.ma_50 != null ? fmtPrice(trendDetail.ma_50) : 'N/A'}</div>
            </div>
            <div className="bg-card-hover rounded p-2">
              <div className="text-muted-foreground text-[10px]">200DMA</div>
              <div className="font-mono">{trendDetail.ma_200 != null ? fmtPrice(trendDetail.ma_200) : 'N/A'}</div>
            </div>
            <div className="bg-card-hover rounded p-2">
              <div className="text-muted-foreground text-[10px]">6M Return</div>
              <div className="font-mono">{fmtPct(trendDetail.return_6m)}</div>
            </div>
            <div className="bg-card-hover rounded p-2">
              <div className="text-muted-foreground text-[10px]">1Y Return</div>
              <div className="font-mono">{fmtPct(trendDetail.return_1y)}</div>
            </div>
          </div>

          {/* Value / Recovery info */}
          {valueDetail.week_52_high && (
            <div className="text-xs text-muted-foreground">
              52W High: <span className="text-foreground font-mono">{fmtPrice(valueDetail.week_52_high)}</span>
              <span className="mx-2">·</span>
              Down {fmtPct(valueDetail.drop_from_high_pct)} from high
              <span className="mx-2">·</span>
              Upside to high {fmtPct(valueDetail.upside_to_high_pct)}
            </div>
          )}

          {/* Positive signals */}
          {stock.positive_signals && stock.positive_signals.length > 0 && (
            <div className="bg-emerald-500/5 border border-emerald-500/15 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-xs font-semibold text-emerald-400">Positive Signals</span>
              </div>
              <ul className="space-y-1">
                {stock.positive_signals.map((p, i) => (
                  <li key={i} className="text-xs text-emerald-200/90 flex gap-2">
                    <span className="text-emerald-500">+</span>{p}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Risk flags */}
          {stock.risk_flags && stock.risk_flags.length > 0 && (
            <div className="bg-red-500/5 border border-red-500/15 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
                <span className="text-xs font-semibold text-red-400">Risk Flags</span>
              </div>
              <ul className="space-y-1">
                {stock.risk_flags.map((r, i) => (
                  <li key={i} className="text-xs text-red-200/90 flex gap-2">
                    <span className="text-red-500">!</span>{r}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Cap reason */}
          {stock.cap_reason && stock.cap_reason.length > 0 && (
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <Info className="w-3.5 h-3.5 text-amber-400" />
                <span className="text-xs font-semibold text-amber-400">Score Capped</span>
              </div>
              <div className="text-[11px] text-amber-200/90 mb-1">
                Calculated: <span className="font-mono">{stock.calculated_score}</span> → Final: <span className="font-mono">{stock.final_score}</span>
              </div>
              <ul className="space-y-0.5">
                {stock.cap_reason.map((r, i) => (
                  <li key={i} className="text-[11px] text-amber-200/90">· {r}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function TiwiStockV2Page() {
  const { user } = useAuth();
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [labelFilter, setLabelFilter] = useState('All');
  const [sectorFilter, setSectorFilter] = useState('All Sectors');
  const [entryFilter, setEntryFilter] = useState('All');
  const [commodityFilter, setCommodityFilter] = useState('All');
  const [stageFilter, setStageFilter] = useState('All');
  const [view, setView] = useState('all'); // 'all' or 'mining'
  const [commodityTrends, setCommodityTrends] = useState([]);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [progress, setProgress] = useState(null);
  const [marketProgress, setMarketProgress] = useState(null);
  const [triggering, setTriggering] = useState(false);
  const [refreshingMarket, setRefreshingMarket] = useState(false);
  const [cacheInfo, setCacheInfo] = useState({ trend: null, fin: null });
  const [totalScored, setTotalScored] = useState(0);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = view === 'mining'
        ? `${API_URL}/api/smart-buy-v2/mining-resources?limit=300`
        : `${API_URL}/api/smart-buy-v2?limit=300`;
      const res = await fetch(url);
      const json = await res.json();
      if (json.success) {
        setStocks(json.data || []);
        if (json.progress) setProgress(json.progress);
      } else {
        setError(json.error || 'Failed to load data');
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [view]);

  // Load commodity trends snapshot when in mining view
  useEffect(() => {
    if (view !== 'mining') return;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/smart-buy-v2/commodity-trends`);
        const json = await res.json();
        if (json.success) setCommodityTrends(json.data || []);
      } catch (_) { /* ignore */ }
    })();
  }, [view]);

  // Poll for progress while a recalculation runs
  useEffect(() => {
    fetchData();
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`${API_URL}/api/smart-buy-v2-status`);
        const json = await res.json();
        if (json.success) {
          setProgress(json.progress);
          setMarketProgress(json.market_refresh_progress);
          setTotalScored(json.total_scored);
          setCacheInfo({
            trend: json.trend_cache,
            fin: json.financial_cache,
          });
        }
      } catch (_) { /* ignore */ }
    }, 5000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const triggerRecalc = async () => {
    setTriggering(true);
    try {
      const res = await authFetch(`${API_URL}/api/admin/recalculate-smart-buy-v2`, { method: 'POST' });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        alert(`Recalculate failed (HTTP ${res.status}): ${json.detail || json.message || 'unknown error'}`);
      } else if (!json.success) {
        alert(json.message || 'Recalculation could not be started');
      } else {
        alert('Score recalculation started — should finish in under a minute (uses cached data).');
      }
    } catch (e) {
      alert(`Failed: ${e.message}`);
    } finally {
      setTriggering(false);
    }
  };

  const triggerMarketRefresh = async (force = false) => {
    setRefreshingMarket(true);
    try {
      const res = await authFetch(`${API_URL}/api/admin/refresh-market-data${force ? '?force=true' : ''}`, { method: 'POST' });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        alert(`Market refresh failed (HTTP ${res.status}): ${json.detail || json.message || 'unknown error'}`);
      } else if (!json.success) {
        alert(json.message || 'Market refresh could not be started');
      } else {
        alert(force
          ? 'FORCE refresh started — re-fetches everything (~10-20 min).'
          : 'Market refresh started — only stale symbols will be re-fetched (~5-10 min).');
      }
    } catch (e) {
      alert(`Failed: ${e.message}`);
    } finally {
      setRefreshingMarket(false);
    }
  };

  const handleMiningOverride = async (symbol, stage) => {
    try {
      const res = await authFetch(`${API_URL}/api/admin/mining-overrides/${symbol}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mining_stage: stage }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        alert(`Override failed (HTTP ${res.status}): ${json.detail || json.message || 'unknown'}`);
        return;
      }
      // Refresh the list so user sees the change
      fetchData();
    } catch (e) {
      alert(`Failed: ${e.message}`);
    }
  };

  const filtered = stocks.filter(s => {
    if (labelFilter !== 'All' && s.final_label !== labelFilter) return false;
    if (sectorFilter !== 'All Sectors' && !(s.sector || '').toLowerCase().includes(sectorFilter.toLowerCase())) return false;
    if (entryFilter !== 'All' && s.entry_quality !== entryFilter) return false;
    if (view === 'mining' && commodityFilter !== 'All' && s.primary_commodity !== commodityFilter) return false;
    if (view === 'mining' && stageFilter !== 'All' && s.mining_stage !== stageFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!(s.symbol || '').toLowerCase().includes(q) && !(s.name || '').toLowerCase().includes(q)) return false;
    }
    return true;
  });

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto" data-testid="tiwistock-v2-page">
      {/* HEADER */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-emerald-500/20 to-yellow-500/20 rounded-lg">
            <Sparkles className="w-6 h-6 text-emerald-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              Tiwistock-2
              <span className="text-[10px] px-1.5 py-0.5 bg-emerald-500/20 text-emerald-300 rounded font-semibold tracking-wider">V2 ✨</span>
            </h1>
            <p className="text-xs text-muted-foreground">
              Smart decision engine · {stocks.length} stocks scored
              {progress?.running && (
                <span className="ml-2 text-emerald-400">
                  · Scoring {progress.current}/{progress.total}…
                </span>
              )}
              {marketProgress?.running && (
                <span className="ml-2 text-blue-400">
                  · Refreshing market data {marketProgress.current}/{marketProgress.total}…
                </span>
              )}
            </p>
          </div>
        </div>

        {user?.role === 'admin' && (
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => triggerMarketRefresh(false)}
              disabled={refreshingMarket || marketProgress?.running}
              className="flex items-center gap-2 px-3 py-2 bg-blue-500/15 text-blue-300 border border-blue-500/30 rounded-lg hover:bg-blue-500/25 transition-colors text-sm disabled:opacity-50"
              data-testid="v2-refresh-market-btn"
              title="Slow: fetch yfinance data — only stale symbols are refreshed"
            >
              <RefreshCw className={`w-4 h-4 ${(refreshingMarket || marketProgress?.running) ? 'animate-spin' : ''}`} />
              {marketProgress?.running
                ? `Refreshing ${marketProgress.current}/${marketProgress.total}`
                : 'Refresh Market Data'}
            </button>
            <button
              onClick={triggerRecalc}
              disabled={triggering || progress?.running}
              className="flex items-center gap-2 px-3 py-2 bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 rounded-lg hover:bg-emerald-500/25 transition-colors text-sm disabled:opacity-50"
              data-testid="v2-recalculate-btn"
              title="Fast: scores from cached data only (<1 min)"
            >
              <Sparkles className={`w-4 h-4 ${(triggering || progress?.running) ? 'animate-pulse' : ''}`} />
              {progress?.running
                ? `Scoring ${progress.current}/${progress.total}`
                : 'Recalculate V2'}
            </button>
          </div>
        )}
      </div>

      {/* CACHE STATUS LINE (admin only) */}
      {user?.role === 'admin' && cacheInfo.trend && (
        <div className="text-[11px] text-muted-foreground mb-3" data-testid="v2-cache-status">
          Trend cache: <span className="font-mono text-foreground">{cacheInfo.trend.count}</span> stocks
          {cacheInfo.trend.last_updated_at && (
            <> · last updated <span className="font-mono text-foreground">{new Date(cacheInfo.trend.last_updated_at).toLocaleString()}</span></>
          )}
          {cacheInfo.fin?.count !== undefined && (
            <> · Financial cache: <span className="font-mono text-foreground">{cacheInfo.fin.count}</span> stocks</>
          )}
        </div>
      )}

      {/* INFO BANNER */}
      <div className="bg-card border border-border rounded-xl p-4 mb-5 text-xs text-muted-foreground" data-testid="v2-info-banner">
        <div className="flex items-start gap-2">
          <Info className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-foreground font-semibold mb-1">SmartBuyScoreV2 weighting</p>
            <p>Analyst 30% · Value/Recovery 20% · Financial Strength 20% · Activity 10% · Trend 10% · Risk 10%</p>
            <p className="mt-1 opacity-80">Hard caps apply: dangerous debt → Avoid; weak fundamentals cap rating; below 50/200DMA caps to Watchlist; etc.</p>
            {user?.role === 'admin' && (
              <p className="mt-2 opacity-80">
                <span className="text-blue-400">Refresh Market Data</span> (slow, ~5-10 min) fetches yfinance trend & financial data.{' '}
                <span className="text-emerald-400">Recalculate V2</span> (fast, &lt;1 min) scores from cached data only.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* SUB-TAB TOGGLE */}
      <div className="flex items-center gap-2 mb-4 border-b border-border" data-testid="v2-subtab-toggle">
        <button
          onClick={() => setView('all')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${view === 'all' ? 'border-emerald-500 text-emerald-300' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          data-testid="v2-tab-all"
        >
          <Layers className="w-4 h-4" /> All Stocks
        </button>
        <button
          onClick={() => setView('mining')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${view === 'mining' ? 'border-amber-500 text-amber-300' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
          data-testid="v2-tab-mining"
        >
          <Pickaxe className="w-4 h-4" /> Mining / Resources
        </button>
      </div>

      {/* Resource Sector Warning + Commodity Snapshot (mining view only) */}
      {view === 'mining' && (
        <>
          <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4 mb-4 text-xs" data-testid="v2-mining-warning">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-amber-300 font-semibold mb-1">Resource Sector Warning</p>
                <p className="text-amber-200/80">
                  Mining and exploration stocks are highly cyclical. High upside to 52-week high does not always mean undervaluation. Check commodity prices, production status, cash flow, debt, and dilution risk before considering a buy.
                </p>
              </div>
            </div>
          </div>

          {commodityTrends.length > 0 && (
            <div className="bg-card border border-border rounded-xl p-4 mb-4" data-testid="v2-commodity-snapshot">
              <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-2 font-semibold">Commodity Trend Snapshot</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
                {commodityTrends.map(c => {
                  const score = c.effective_score;
                  const isOverride = c.override_score !== null && c.override_score !== undefined;
                  const color = score === null || score === undefined ? 'text-muted-foreground'
                    : score >= 70 ? 'text-emerald-400'
                    : score >= 35 ? 'text-yellow-400'
                    : 'text-red-400';
                  return (
                    <div key={c.commodity} className="bg-card-hover rounded p-2 text-xs">
                      <div className="text-muted-foreground text-[10px] flex items-center justify-between">
                        <span>{c.commodity}</span>
                        {isOverride && <span className="text-blue-300 text-[9px] uppercase">manual</span>}
                      </div>
                      <div className={`font-mono text-base ${color}`}>{score !== null && score !== undefined ? score.toFixed(0) : '—'}</div>
                      {c.auto_return_3m_pct !== null && c.auto_return_3m_pct !== undefined && (
                        <div className="text-[10px] text-muted-foreground">3M {c.auto_return_3m_pct >= 0 ? '+' : ''}{c.auto_return_3m_pct.toFixed(1)}%</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {/* FILTERS */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search ticker or company…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-card border border-border rounded-lg text-sm focus:outline-none focus:border-emerald-500/50"
            data-testid="v2-search-input"
          />
        </div>
        <select
          value={labelFilter}
          onChange={(e) => setLabelFilter(e.target.value)}
          className="px-3 py-2 bg-card border border-border rounded-lg text-sm focus:outline-none"
          data-testid="v2-label-filter"
        >
          {LABELS.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
        {view === 'all' && (
          <select
            value={sectorFilter}
            onChange={(e) => setSectorFilter(e.target.value)}
            className="px-3 py-2 bg-card border border-border rounded-lg text-sm focus:outline-none"
            data-testid="v2-sector-filter"
          >
            {SECTORS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
        <select
          value={entryFilter}
          onChange={(e) => setEntryFilter(e.target.value)}
          className="px-3 py-2 bg-card border border-border rounded-lg text-sm focus:outline-none"
          data-testid="v2-entry-filter"
        >
          {ENTRIES.map(e => <option key={e} value={e}>{e}</option>)}
        </select>
        {view === 'mining' && (
          <>
            <select
              value={commodityFilter}
              onChange={(e) => setCommodityFilter(e.target.value)}
              className="px-3 py-2 bg-card border border-border rounded-lg text-sm focus:outline-none"
              data-testid="v2-commodity-filter"
            >
              {COMMODITIES.map(c => <option key={c} value={c}>{c === 'All' ? 'All Commodities' : c}</option>)}
            </select>
            <select
              value={stageFilter}
              onChange={(e) => setStageFilter(e.target.value)}
              className="px-3 py-2 bg-card border border-border rounded-lg text-sm focus:outline-none"
              data-testid="v2-stage-filter"
            >
              {MINING_STAGES.map(s => <option key={s} value={s}>{s === 'All' ? 'All Stages' : s}</option>)}
            </select>
          </>
        )}
      </div>

      <p className="text-xs text-muted-foreground mb-3">{filtered.length} stocks</p>

      {/* STATES */}
      {loading ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center">
          <Loader2 className="w-8 h-8 text-emerald-400 mx-auto mb-3 animate-spin" />
          <p className="text-sm text-muted-foreground">Loading SmartBuy V2 scores…</p>
        </div>
      ) : error ? (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-sm text-red-300">
          {error}
        </div>
      ) : stocks.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center" data-testid="v2-empty-state">
          <Sparkles className="w-10 h-10 text-emerald-400/60 mx-auto mb-3" />
          <h2 className="text-base font-semibold mb-2">No V2 scores yet</h2>
          <p className="text-sm text-muted-foreground mb-1">
            {user?.role === 'admin'
              ? (cacheInfo.trend?.count > 0
                  ? 'Cached market data available. Click "Recalculate V2" to compute scores (~30-60 seconds).'
                  : 'No market data cached yet. Click "Refresh Market Data" first (~5-10 min), then "Recalculate V2".')
              : 'The V2 engine has not been run yet. Please check back later.'}
          </p>
          {progress?.running && (
            <p className="text-xs text-emerald-400 mt-2">Scoring in progress: {progress.current}/{progress.total}</p>
          )}
          {marketProgress?.running && (
            <p className="text-xs text-blue-400 mt-2">Market data refresh: {marketProgress.current}/{marketProgress.total}</p>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(stock => (
            <StockCard
              key={stock.symbol}
              stock={stock}
              expanded={expanded === stock.symbol}
              onToggle={() => setExpanded(expanded === stock.symbol ? null : stock.symbol)}
              isAdmin={user?.role === 'admin'}
              onMiningOverride={user?.role === 'admin' ? handleMiningOverride : null}
            />
          ))}
        </div>
      )}

      {/* Footer note */}
      <p className="text-[11px] text-muted-foreground text-center mt-8 opacity-60">
        Tiwistock-2 runs alongside the original Smart Rankings — both models stay available for comparison.
      </p>
    </div>
  );
}
